import React from "react";
import { useIntl } from "react-intl";

import { useFormik } from "formik";

import generateOtpService from "@app/services/auth/generateOtpService";
import verifyOtpService from "@app/services/auth/verifyOtpService";

import { useCountdown } from "@hooks";

import { OTP_SCOPES } from "@utils/enum";

import {
  OTP_FORM_INITIAL_VALUES,
  RESET_CODE_COUNTDOWN_LIMIT,
  INVALID_OTP_ERROR,
} from "./OTPForm.constants";
import { OtpValidationSchema } from "./OTPForm.schema";
import { buildPayloadByScope } from "./OTPForm.utils";

export const useOTPForm = ({ id, email, scope, onError, onSubmit }) => {
  const { countdown: resendCodeCountdown, resetCountdown } = useCountdown(
    RESET_CODE_COUNTDOWN_LIMIT
  );
  const { formatMessage, messages } = useIntl();

  const [isLoading, setIsLoading] = React.useState(false);
  const [errorMessage, setErrorMessage] = React.useState("");

  const isResendAvailable = React.useMemo(
    () => resendCodeCountdown === 0,
    [resendCodeCountdown]
  );

  const resendLinkLabel = React.useMemo(() => {
    if (resendCodeCountdown === 0) {
      return messages.label_resend_code;
    }

    return formatMessage(
      { id: "label_resend_code_in" },
      { seconds: resendCodeCountdown }
    );
  }, [messages.label_resend_code, resendCodeCountdown, formatMessage]);

  const handleRequestOtpOnEmail = React.useCallback(async () => {
    try {
      setIsLoading(true);

      const payload = buildPayloadByScope(scope, { email, id });

      await generateOtpService(payload);

      resetCountdown();
    } catch {
      onError(messages.exception_error_message);
    } finally {
      setIsLoading(false);
    }
  }, [
    email,
    id,
    messages.exception_error_message,
    onError,
    resetCountdown,
    scope,
  ]);

  const handleSubmit = React.useCallback(
    async values => {
      try {
        setIsLoading(true);

        const payload = buildPayloadByScope(scope, {
          id,
          email,
        });

        payload.otp = values.otp;

        const { status, data: authData } = await verifyOtpService(payload);

        if (status !== 200) {
          onError(messages.exception_error_message);
          return;
        }

        onSubmit(authData);
      } catch (error) {
        const isInvalidOtpError =
          error?.response?.data?.message === INVALID_OTP_ERROR;

        if (isInvalidOtpError) {
          setErrorMessage(messages.error_invalid_otp);

          return;
        }

        setErrorMessage(messages.exception_error_message);
      } finally {
        setIsLoading(false);
      }
    },
    [
      email,
      id,
      messages.error_invalid_otp,
      messages.exception_error_message,
      onError,
      onSubmit,
      scope,
    ]
  );

  const formik = useFormik({
    initialValues: OTP_FORM_INITIAL_VALUES,
    validationSchema: OtpValidationSchema,
    onSubmit: handleSubmit,
  });

  const handleOtpChange = React.useCallback(
    value => {
      formik.setFieldValue("otp", value);
    },
    [formik]
  );

  React.useEffect(() => {
    if (scope === OTP_SCOPES.LOGIN_USER) {
      return;
    }

    handleRequestOtpOnEmail();
  }, [handleRequestOtpOnEmail, scope]);

  return {
    errorMessage,
    formik,
    isLoading,
    isResendAvailable,
    resendLinkLabel,
    handleRequestOtpOnEmail,
    handleOtpChange,
  };
};
